/**
 * O QUE A PESQUISA ENCONTROU, ESTRUTURADO NA ORIGEM (04/10).
 *
 * Cada quadro `found` do fio da missão é uma descoberta: um lugar, uma empresa,
 * um perfil, uma foto, uma cor. O servidor já os tem estruturados antes de os
 * formatar em texto para o modelo — o front nunca os lê de volta da prosa.
 *
 * Partilhado porque são duas pontas: o redutor do fio (agents) dobra-os, e o
 * dossiê do primeiro dia (onboarding) desenha-os. Puro: tipos, a leitura do
 * quadro e o agrupamento — sem Vue, sem palavras.
 */

export type Network = "instagram" | "facebook" | "tiktok" | "linkedin" | "youtube" | "x" | "site";

interface Base {
	id: string;
	source?: string;
	at?: string;
}

export interface PlaceFound extends Base {
	kind: "place";
	name: string;
	category?: string;
	address?: string;
	phone?: string;
	website?: string;
	rating?: number;
	reviews?: number;
	hours?: string[];
	mapsUrl?: string;
	photos?: string[];
}
export interface CompanyFound extends Base {
	kind: "company";
	cnpj: string;
	legalName?: string;
	tradeName?: string;
	activity?: string;
	secondary?: string[];
	openedAt?: string;
	status?: string;
	address?: string;
	partners?: string[];
	capital?: number | string;
}
export interface ProfileFound extends Base {
	kind: "profile";
	network: Network | string;
	handle?: string;
	name?: string;
	url?: string;
	followers?: number;
	following?: number;
	posts?: number;
	bio?: string;
	avatar?: string;
}
export interface PostFound extends Base {
	kind: "post";
	network: string;
	url?: string;
	image?: string;
	caption?: string;
	likes?: number;
	comments?: number;
}
export interface ReviewFound extends Base {
	kind: "review";
	source: string;
	author?: string;
	text: string;
	rating?: number;
}
export interface ImageFound extends Base {
	kind: "image";
	url: string;
	alt?: string;
	page?: string;
}
export interface ContactFound extends Base {
	kind: "contact";
	channel: "phone" | "whatsapp" | "email" | "address" | string;
	value: string;
	label?: string;
}
export interface PaletteFound extends Base {
	kind: "palette";
	colors: string[];
	logo?: string;
}
export interface PageFound extends Base {
	kind: "page";
	url: string;
	title?: string;
}
export interface SearchFound extends Base {
	kind: "search";
	query: string;
	results?: number;
}
export interface FactFound extends Base {
	kind: "fact";
	label: string;
	value: string;
}

export type Discovery =
	| PlaceFound
	| CompanyFound
	| ProfileFound
	| PostFound
	| ReviewFound
	| ImageFound
	| ContactFound
	| PaletteFound
	| PageFound
	| SearchFound
	| FactFound;

/** O campo obrigatório de cada tipo — sem ele o quadro não desenha nada e cai. */
const REQUIRED: Record<Discovery["kind"], string> = {
	place: "name",
	company: "cnpj",
	profile: "network",
	post: "network",
	review: "text",
	image: "url",
	contact: "value",
	palette: "colors",
	page: "url",
	search: "query",
	fact: "value",
};

/** Um quadro `found` como descoberta, ou null quando ele não diz o mínimo. */
export function toDiscovery(payload: Record<string, unknown>): Discovery | null {
	const kind = payload.kind as Discovery["kind"];
	const required = REQUIRED[kind];
	if (typeof payload.id !== "string" || !payload.id || !required) return null;
	const value = payload[required];
	if (value === undefined || value === null || value === "") return null;
	if (kind === "palette" && !Array.isArray(value)) return null;
	return payload as unknown as Discovery;
}

/**
 * As descobertas GRAVADAS (nos passos de todas as missões do percurso) e as que
 * o fio traz agora, numa lista só: a ordem de chegada, o mesmo `id` atualizado
 * no lugar pela mais recente. Um item gravado que não diz o mínimo cai.
 */
export function mergeFound(...lists: ReadonlyArray<readonly unknown[]>): Discovery[] {
	const out: Discovery[] = [];
	const at = new Map<string, number>();
	for (const list of lists)
		for (const raw of list) {
			const item =
				raw && typeof raw === "object" ? toDiscovery(raw as Record<string, unknown>) : null;
			if (!item) continue;
			const i = at.get(item.id);
			if (i === undefined) {
				at.set(item.id, out.length);
				out.push(item);
			} else out[i] = { ...out[i], ...item } as Discovery;
		}
	return out;
}

/** 14 dígitos como se escreve um CNPJ: 00.000.000/0000-00. O resto volta como veio. */
export function formatCnpj(raw: string): string {
	const d = raw.replace(/\D/g, "");
	if (d.length !== 14) return raw;
	return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

/** Um hex que o CSS aceita (#rgb, #rrggbb, #rrggbbaa) — a cor é DADO e só passa limpa. */
export function isHex(color: string): boolean {
	return /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(color.trim());
}

export interface Dossier {
	places: PlaceFound[];
	companies: CompanyFound[];
	contacts: ContactFound[];
	profiles: ProfileFound[];
	posts: PostFound[];
	/** Toda foto vista, sem repetidas: as soltas, as do lugar e as dos posts. */
	photos: string[];
	reviews: ReviewFound[];
	palette: PaletteFound | null;
	pages: PageFound[];
	searches: SearchFound[];
	facts: FactFound[];
}

/** As descobertas na ordem em que chegaram, arrumadas por secção do dossiê. */
export function dossierOf(found: readonly Discovery[]): Dossier {
	const d: Dossier = {
		places: [],
		companies: [],
		contacts: [],
		profiles: [],
		posts: [],
		photos: [],
		reviews: [],
		palette: null,
		pages: [],
		searches: [],
		facts: [],
	};
	const seen = new Set<string>();
	const photo = (url?: string) => {
		if (url && !seen.has(url)) {
			seen.add(url);
			d.photos.push(url);
		}
	};
	for (const item of found) {
		switch (item.kind) {
			case "place":
				d.places.push(item);
				for (const url of item.photos ?? []) photo(url);
				break;
			case "company":
				d.companies.push(item);
				break;
			case "contact":
				d.contacts.push(item);
				break;
			case "profile":
				d.profiles.push(item);
				break;
			case "post":
				d.posts.push(item);
				photo(item.image);
				break;
			case "image":
				photo(item.url);
				break;
			case "review":
				d.reviews.push(item);
				break;
			case "palette":
				// A mais recente vence: a captura do logotipo afina a do site.
				d.palette = { ...item, colors: item.colors.filter(isHex) };
				break;
			case "page":
				d.pages.push(item);
				break;
			case "search":
				d.searches.push(item);
				break;
			case "fact":
				d.facts.push(item);
				break;
		}
	}
	return d;
}
